import { useEffect, useState } from "react";
import { useModalStore } from "../../../stores/modalStore.js";
import { useServerStore } from "../../../stores/serverStore.js";
import { useAuditStore } from "../../../stores/auditStore.js";
import { useFocusTrap } from "../../../hooks/useFocusTrap.js";

const ACTION_META = {
  member_kick:      { icon: '👢', label: 'Member Kicked',   color: '#f59e0b' },
  member_ban:       { icon: '🔨', label: 'Member Banned',   color: '#ef4444' },
  member_unban:     { icon: '✅', label: 'Member Unbanned', color: '#22c55e' },
  role_change:      { icon: '⚡', label: 'Role Changed',    color: '#6366f1' },
  message_delete:   { icon: '🗑️', label: 'Message Deleted', color: '#f43f5e' },
  channel_create:   { icon: '➕', label: 'Channel Created', color: '#06b6d4' },
  channel_delete:   { icon: '🗑️', label: 'Channel Deleted', color: '#f43f5e' },
  invite_generate:  { icon: '🔗', label: 'Invite Generated',color: '#8b5cf6' },
  server_edit:      { icon: '✏️', label: 'Server Edited',   color: '#6366f1' },
  automod_trigger:  { icon: '🤖', label: 'AutoMod Trigger', color: '#f59e0b' },
  slow_mode_set:    { icon: '🐌', label: 'Slow Mode Set',   color: '#06b6d4' },
};

function timeAgo(ts) {
  const diff = Date.now() - new Date(ts).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function AuditLogModal() {
  const { isOpen, type, onClose } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);
  const localEntries    = useAuditStore(s => s.entries);

  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [filter,  setFilter]    = useState('all');
  const isModalOpen = isOpen && type === 'auditLog';
  const trapRef = useFocusTrap(isModalOpen);

  useEffect(() => {
    if (!isModalOpen || !currentServerId) return;
    // Always seed with local audit store first
    setEntries(localEntries);
    if (currentServerId.startsWith('demo-')) return;
    setLoading(true);
    fetch(`/api/servers/${currentServerId}/audit?limit=100`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
    })
      .then(r => r.json())
      .then(d => {
        const apiEntries = d.entries || [];
        setEntries(apiEntries.length > 0 ? [...apiEntries, ...localEntries] : localEntries);
        setLoading(false);
      })
      .catch(() => { setEntries(localEntries); setLoading(false); });
  }, [isModalOpen, currentServerId, localEntries]);

  if (!isModalOpen) return null;

  const filtered = filter === 'all' ? entries : entries.filter(e => e.action === filter);
  const actions  = [...new Set(entries.map(e => e.action))];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
      onClick={onClose}
    >
      <div ref={trapRef} role="dialog" aria-modal="true" aria-label="Audit Log"
        style={{ background: 'rgb(var(--aethex-panel))', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, width: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.6)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.07)', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Audit Log</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{entries.length} entries</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Filter */}
        <div style={{ padding: '8px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 6, flexWrap: 'wrap', flexShrink: 0 }}>
          {['all', ...actions].map(a => (
            <button key={a} onClick={() => setFilter(a)}
              style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: filter === a ? 'rgb(var(--aethex-primary))' : 'rgba(255,255,255,0.07)', color: filter === a ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'all 0.15s' }}
            >
              {a === 'all' ? 'All' : (ACTION_META[a]?.label || a)}
            </button>
          ))}
        </div>

        {/* Entries */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 120, color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, color: 'rgba(255,255,255,0.25)', gap: 8 }}>
              <span style={{ fontSize: 32 }}>📋</span>
              <span style={{ fontSize: 13 }}>No audit entries yet</span>
            </div>
          ) : filtered.map(e => {
            const meta = ACTION_META[e.action] || { icon: '📝', label: e.action, color: 'rgba(255,255,255,0.5)' };
            return (
              <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', transition: 'background 0.1s' }}
                onMouseEnter={el => el.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                onMouseLeave={el => el.currentTarget.style.background = 'none'}
              >
                {/* Icon */}
                <div style={{ width: 32, height: 32, borderRadius: 8, background: `${meta.color}20`, border: `1px solid ${meta.color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
                  {meta.icon}
                </div>
                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: meta.color }}>{meta.label}</span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>{timeAgo(e.timestamp)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)' }}>
                    <strong style={{ color: 'rgba(255,255,255,0.85)' }}>{e.actorName || 'System'}</strong>
                    {e.targetName && <> → <strong style={{ color: 'rgba(255,255,255,0.75)' }}>{e.targetName}</strong></>}
                  </div>
                  {e.detail && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{e.detail}</div>}
                </div>
                {/* Time */}
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.2)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {new Date(e.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
